import { Tooltip } from '@mantine/core';
import { Icon } from '@uag/react-core';
import {
    Actions,
    Layout,
    Model,
    TabSetNode,
    type BorderNode,
    type ITabSetRenderValues,
    type TabNode
} from 'flexlayout-react';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentContainer } from 'app/modules/wallforming/components/content-container/ContentContainer';
import { mainModelJson } from 'app/modules/wallforming/components/data/mainModelJson';
import { wallFormingModelJson } from 'app/modules/wallforming/components/data/wallFormingModelJson';
import { SettingContainer } from 'app/modules/wallforming/components/setting-container/SettingContainer';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import 'flexlayout-react/style/light.css';
import 'app/modules/wallforming/custom-styles/customFlexlayoutStyle.css';

const mainModel = Model.fromJson(mainModelJson);
const wallFormingModel = Model.fromJson(wallFormingModelJson);

export const FlexLayoutContainer = () => {
    const { t } = useTranslation();
    const layoutRef = useRef<Layout | null>(null);
    const settingModelRef = useRef<Model | null>(null);

    const wallFormingFactory = useCallback((node: TabNode) => {
        const component = node.getComponent();

        if (component === 'content') {
            return (
                <div className="flex flex-col w-full h-full overflow-hidden p-2">
                    <ContentContainer key="content" />
                </div>
            );
        }
    }, []);

    const mainFactory = useCallback(
        (node: TabNode) => {
            const component = node.getComponent();

            if (component === 'wallForming') {
                return (
                    <div className="sublayout wallforming relative w-full h-full bg-background-default m-0 p-0 overflow-hidden">
                        <Layout
                            key="r3f-layout"
                            factory={wallFormingFactory}
                            model={wallFormingModel}
                            realtimeResize={false}
                        />
                    </div>
                );
            }
            if (component === 'settings') {
                return (
                    <div className="relative w-full h-full bg-background-default m-0 p-0 overflow-hidden">
                        <SettingContainer settingModelRef={settingModelRef} />
                    </div>
                );
            }
        },
        [wallFormingFactory]
    );

    const resetNodeSize = useCallback((model: Model) => {
        model.visitNodes((node) => {
            if (node instanceof TabSetNode && node.isMaximized()) {
                model.doAction(Actions.maximizeToggle(node.getId()));
            }
        });
    }, []);

    const resetLayout = useCallback(() => {
        const screenWidth = window.screen.width;

        mainModel.doAction(Actions.updateNodeAttributes('content-tabset', { weight: screenWidth >= 1920 ? 60 : 50 }));
        mainModel.doAction(Actions.updateNodeAttributes('setting-tabset', { weight: screenWidth >= 1920 ? 25 : 30 }));

        wallFormingModel.doAction(Actions.updateNodeAttributes('content-tab', { weight: 100 }));

        if (settingModelRef.current) {
            settingModelRef.current.doAction(Actions.updateNodeAttributes('options-tab', { weight: 65 }));
            settingModelRef.current.doAction(Actions.updateNodeAttributes('preview-tab', { weight: 35 }));
            resetNodeSize(settingModelRef.current);
        }

        resetNodeSize(mainModel);
        resetNodeSize(wallFormingModel);
    }, [resetNodeSize]);

    const resizeTab = useCallback((tabSetNode: TabSetNode | BorderNode) => {
        if (layoutRef.current) {
            const model = layoutRef.current.props.model;
            const node = model.getNodeById(tabSetNode.getId());

            if (node && node instanceof TabSetNode) {
                model.doAction(Actions.maximizeToggle(node.getId()));
            }
        }
    }, []);

    const onRenderTabSet = useCallback(
        (tabSetNode: TabSetNode | BorderNode, renderValues: ITabSetRenderValues) => {
            const isTabMaximized = tabSetNode.isMaximized();
            renderValues.buttons.push(
                <Tooltip key="reset-layout-button" label={t('labelResetLayout')}>
                    <Icon
                        className="flexlayout_button justify-center rounded hover:bg-background-action-hover"
                        icon={'restart_alt'}
                        size="xl"
                        onClick={resetLayout}
                    />
                </Tooltip>,
                <Tooltip key="resize-tab-button" label={isTabMaximized ? t('labelMinimizeTab') : t('labelMaximizeTab')}>
                    <Icon
                        className="flexlayout_button justify-center rounded hover:bg-background-action-hover"
                        icon={isTabMaximized ? 'close_fullscreen' : 'open_in_full'}
                        size="xl"
                        onClick={() => resizeTab(tabSetNode)}
                    />
                </Tooltip>
            );
        },
        [resetLayout, resizeTab, t]
    );

    useEffect(() => {
        const subscribedSettingView = usePlacementStore.subscribe(
            (state) => state.selectedSettingView,
            (newSettingView) => {
                mainModel.doAction(
                    Actions.updateNodeAttributes('setting-tab', {
                        name:
                            newSettingView === SettingView.AUTOMATED_PLACEMENT
                                ? t('labelAutomatedPlacement')
                                : t('labelCatalogue')
                    })
                );
            }
        );

        return () => {
            subscribedSettingView();
        };
    }, [t]);

    return (
        <div className="relative w-full h-full bg-background-default border border-background-default overflow-hidden">
            <Layout
                ref={layoutRef}
                factory={mainFactory}
                model={mainModel}
                realtimeResize={false}
                onRenderTabSet={onRenderTabSet}
            />
        </div>
    );
};
